/**
 * AuthGate — Guards the app on authentication state in standalone mode.
 *
 * In STANDALONE mode: Calls `initAuth()` on mount. While loading, shows a spinner.
 * If unauthenticated, redirects to the AngularJS login page.
 * If authenticated, renders children.
 *
 * In EMBEDDED mode: Renders children immediately — AngularJS handles auth.
 */

import { useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { getMountConfig } from '@/mountConfig';
import { useAuthStore, type AuthStatus } from '@/store/authStore';
import { redirectToLogin } from '@/lib/authApi';
import { consumeIntendedDestination } from '@/lib/loginRedirect';
import { UiLoader } from '@/ui';

/**
 * Vendor routes use their own Auth0 PKCE flow + RoleMaster check inside
 * VendorAuthGuard. Skipping the staff RoleMaster path here mirrors the
 * AngularJS auth interceptor (public/SPIGlassApp/app.js line 28-43), which
 * bailed out of the Login redirect for `/VendorConnect/*` paths and when
 * `vc_auth_pending === 'vendor'` was set.
 */
function isVendorAuthContext(pathname: string): boolean {
  if (pathname.startsWith('/vendor-connect') || pathname.startsWith('/VendorConnect')) {
    return true;
  }
  try {
    return sessionStorage.getItem('vc_auth_pending') === 'vendor';
  } catch {
    return false;
  }
}

interface AuthGateProps {
  children: ReactNode;
}

export function AuthGate({ children }: AuthGateProps) {
  const { mode } = getMountConfig();
  const location = useLocation();
  const status = useAuthStore((s) => s.status);
  const initAuth = useAuthStore((s) => s.initAuth);

  // In embedded mode, skip auth gate entirely — AngularJS owns auth
  if (mode === 'embedded') {
    return <>{children}</>;
  }

  // Vendor routes have their own PKCE auth handled by VendorAuthGuard.
  // Running the staff initAuth would call RoleMaster expecting SPI staff
  // permissions and either land vendors at /Home/dashboard or surface the
  // "Authentication failed" screen after a successful Auth0 callback.
  if (isVendorAuthContext(location.pathname)) {
    return <>{children}</>;
  }

  // Standalone mode — authenticate via RoleMaster + Permissions API.
  // On localhost dev mode with Vite proxy, the proxy injects the Bearer cookie
  // from .env.development, so the full auth flow works the same as production.
  return (
    <StandaloneAuthGate status={status} initAuth={initAuth}>
      {children}
    </StandaloneAuthGate>
  );
}

/**
 * Inner component that handles standalone auth lifecycle.
 * Separated to avoid running the effect in embedded mode.
 */
function StandaloneAuthGate({
  status,
  initAuth,
  children,
}: {
  status: AuthStatus;
  initAuth: (force?: boolean) => Promise<boolean>;
  children: ReactNode;
}) {
  useEffect(() => {
    if (status === 'idle') {
      initAuth();
    }
  }, [status, initAuth]);

  // NOTE: the 5-minute keepalive and tab-visibility revalidate were intentionally
  // removed. They called `getfunctionsbytoken` periodically with the goal of
  // sliding the Bearer cookie's idle-TTL forward (the same incidental effect
  // the hybrid AngularJS app got from its ambient polls). In our environment
  // RoleMaster does NOT slide the cookie on that call, so the ping never
  // extended the session — it only surfaced expiry sooner and triggered an
  // unwanted recovery bounce every few minutes.
  //
  // With this removed, the cookie still expires on its server-side schedule,
  // but we only DISCOVER the expiry when the user actually does something —
  // the next axiosClient request 401s and the interceptor routes through
  // beginReauth() to bounce through Windows Auth + restore the route. Idle
  // tabs no longer spontaneously redirect. If the underlying cookie TTL
  // really is the problem, the fix has to come from the auth server (longer
  // TTL or an actual refresh endpoint).

  // Phase E coexistence: after the prod Windows-Auth bounce-back lands the user
  // at the app root, restore the URL they originally pasted. `LoginPage` covers
  // the dev-path sign-in; this covers the case where AuthGate is the first
  // component to see the user authenticated (no LoginPage in between).
  // `consumeIntendedDestination()` is single-shot, so this is safe to call
  // unconditionally on every authenticated render.
  useEffect(() => {
    if (status !== 'authenticated') return;
    const target = consumeIntendedDestination();
    if (!target) return;
    // HashRouter listens for `hashchange` — this is a clean nav without
    // touching React Router's history stack from outside.
    window.location.hash = target.startsWith('/') ? `#${target}` : `#/${target}`;
  }, [status]);

  // Loading — show spinner while validating session
  if (status === 'idle' || status === 'loading') {
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <UiLoader />
      </div>
    );
  }

  // Unauthenticated — redirect to login
  if (status === 'unauthenticated') {
    setTimeout(() => redirectToLogin(), 0);
    return (
      <div className='flex items-center justify-center min-h-screen'>
        <p className='text-angular-primary text-lg'>Redirecting to login...</p>
      </div>
    );
  }

  // Error — show a retry-able error message
  if (status === 'error') {
    return (
      <div className='flex flex-col items-center justify-center min-h-screen gap-4'>
        <p className='text-angular-red text-lg'>Authentication failed. Please try again.</p>
        <button
          type='button'
          className='px-4 py-2 bg-angular-blue text-white rounded hover:opacity-90'
          onClick={() => initAuth(true)}
        >
          Retry
        </button>
      </div>
    );
  }

  // Authenticated — render the app
  return <>{children}</>;
}
